import { gql } from "lib/apolloClient";

const assessmentFragment = gql`
  fragment assessmentItem on Assessment {
    id
    type
    isImported
    title {
      id
      value
    }
    academicTerm {
      id
      label
      lockingStatus
    }
    assessmentType {
      id
      value
    }
    image {
      id
      value
    }
    category {
      id
      label
    }
    globalCategories {
      id
      name
    }
    lockingInfo {
      isLocked
    }
    isSyncedFromBluePrintCourse(filters: $syncedBluePrintItemFilters)
    isSyncedWithAllTeacherCourseV2(filters: $syncedBluePrintItemFilters) {
      status
      isConflictExists
      courseSyncStatus {
        course {
          id
          title
        }
        status
      }
    }
    assignments {
      edges {
        id
        state {
          state
        }
        course {
          id
        }
      }
    }
  }
`;

const attachmentFragment = gql`
  fragment attachmentItem on Attachment {
    id
    attachmentType: type
    attachmentTitle: title
    name
    mimeType
    url
    signedUrl
    thumbUrl
    __typename
  }
`;

const unitPlanFragment = gql`
  fragment unitPlanItem on UnitPlan {
    id
    title {
      id
      value
    }
  }
`;

const childrenHierarchyInfoFragment = gql`
  fragment childrenHierarchyInfo on LearningCourseFlow {
    childrenHierarchyInfo(filters: $filters) {
      maxDepth
      totalCount
      totalCountWithFolder
    }
  }
`;

const unitFlowItem = gql`
  fragment unitFlowItem on LearningCourseFlow {
    id
    label
    displaySequence
    isRepeated
    repeatedResources
    depth
    resourceType: itemType
    item {
      ... on Assessment {
        ...assessmentItem
      }
      ... on Attachment {
        ...attachmentItem
      }
      ... on UnitPlan {
        ...unitPlanItem
      }
    }
    parent {
      id
    }
    lockingInfo {
      isLocked
    }
  }
  ${assessmentFragment}
  ${attachmentFragment}
  ${unitPlanFragment}
`;

const unitFlowItemWithoutDepth = gql`
  fragment unitFlowItem on LearningCourseFlow {
    id
    label
    displaySequence
    isRepeated
    repeatedResources
    resourceType: itemType
    item {
      ... on Assessment {
        ...assessmentItem
      }
      ... on Attachment {
        ...attachmentItem
      }
      ... on UnitPlan {
        ...unitPlanItem
      }
    }
    parent {
      id
    }
    state
    sharedDetails {
      class {
        id
        title
      }
      students {
        id
      }
    }
    lockingInfo {
      isLocked
    }
    isSyncedFromBluePrintCourse(filters: $syncedBluePrintItemFilters)
    isSyncedWithAllTeacherCourseV2(filters: $syncedBluePrintItemFilters) {
      status
      isConflictExists
      courseSyncStatus {
        course {
          id
          title
        }
        status
      }
    }
  }
  ${assessmentFragment}
  ${attachmentFragment}
  ${unitPlanFragment}
`;

export const unitFlowFragment = {
  unitFlowItem,
  unitFlowWithChildren: gql`
    fragment unitFlowWithChildrenItem on LearningCourseFlow {
      ...unitFlowItem
      childrenV2: children(filters: $filters) {
        id
        label
        resourceType: itemType
        displaySequence
      }
      ...childrenHierarchyInfo
    }
    ${unitFlowItem}
    ${childrenHierarchyInfoFragment}
  `,
  childrenHierarchyInfoFragment,
};

export const unitFlowFragmentWithoutDepth = {
  unitFlowWithChildren: gql`
    fragment unitFlowWithChildrenItem on LearningCourseFlow {
      ...unitFlowItem
      childrenV2: children(filters: $filters) {
        id
        label
        resourceType: itemType
        displaySequence
      }
      ...childrenHierarchyInfo
    }
    ${unitFlowItemWithoutDepth}
    ${childrenHierarchyInfoFragment}
  `,
};

export const unitFlowOutlineFragment = {
  unitFlowOutlineItem: gql`
    fragment unitFlowOutlineItem on LearningCourseFlow {
      id
      resourceType: itemType
      childrenV2: children(filters: $filters) {
        id
      }
      parent {
        id
      }
      item {
        ... on Assessment {
          title {
            value
          }
        }
      }
    }
  `,
};

export const unitResourceFragment = gql`
  fragment unitResourceItem on LearningCourseFlow {
    id
    label
    resourceType: itemType
    lockingInfo {
      isLocked
    }
    attachment: item {
      ... on Attachment {
        id
        type
        title
        name
        mimeType
        url
        signedUrl
        thumbUrl
        __typename
      }
    }
  }
`;
